import React, { useState } from 'react';
import Alert from 'components/ui/Alert';
import ErrorAlert from 'components/ui/ErrorAlert';
import LoadingCircle from 'components/ui/LoadingCircle';
import Container from 'components/v2/container';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import CargomaticInput from 'components/v2/input';
import Badge from 'components/v2/Badge';
import { CargomaticDropzone } from 'components/v2/Dropzone';
import { uploadBulkShipmentRerate } from 'utils/ShipmentWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import FileDownload from 'js-file-download';
import { useTranslation } from 'react-i18next';
import validator from 'email-validator';
import { sluggify } from 'utils/localization/utils';

const csvTemplate =
  'reference_number,shipper_cost_rerate,shipper_fsc_rerate,carrier_cost_rerate,carrier_fsc_rerate\nSHI-123,Y,Y,Y,Y';

const BulkShipmentRerate = () => {
  const { t } = useTranslation();
  const user = SessionStore.getUser();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [emails, setEmails] = useState(user.email || '');
  const [files, setFiles] = useState([]);
  const dropzoneStyle = {
    display: 'flex',
    height: '50px',
    width: 'auto',
    border: '1px dashed black',
    borderRadius: '4px',
    cursor: 'pointer',
  };

  function handleCloseSuccessAlert() {
    setSuccess('');
  }

  function addFiles(files) {
    if (files) {
      setFiles(files);
    }
  }

  function removeFiles() {
    setFiles([]);
  }

  function handleEmailAddress(value) {
    setEmails(value);
  }

  function handleCloseErrorAlert() {
    setError('');
  }

  function renderErrors() {
    if (Object.keys(errors).length === 0) {
      return null;
    }

    return (
      <Alert type="error" className="my-4">
        <ul>
          {Object.values(errors).map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </Alert>
    );
  }

  async function handleUpload() {
    setErrors({});
    setError('');
    setSuccess('');
    const validationErrors = {};
    if (files.length === 0) {
      validationErrors.no_file_attached = t('ATTACH_VALID_CSV_FILE');
    }
    const emailAddresses = emails.split(',').map((email) => email.trim());
    const emailsValid = emailAddresses.every((email) =>
      validator.validate(email),
    );
    if (emails.length === 0 || !emailsValid) {
      validationErrors.email_error = t('PLEASE_PROVIDE_VALID_EMAILS');
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setPending(true);

    try {
      await uploadBulkShipmentRerate(files[0], emailAddresses);
      setSuccess(t('DATA_UPLOADED_WAIT_FOR_EMAIL'));
    } catch (e) {
      const errorMsg = e?.response?.body?.message || 'SOMETHING_WENT_WRONG';
      setError(t(sluggify(errorMsg)));
    } finally {
      setPending(false);
    }
  }

  if (!SessionStore.isAdmin()) {
    return (
      <Container className="py-10">
        <Alert title={t('NO_PERMISSION_FOR_VIEW')} type="error" />
      </Container>
    );
  }
  return (
    <Container className="py-10">
      <div className="flex space-x-4 items-center">
        <h3 className="my-0 mb-2">{t('BULK_RERATE_SHIPMENT')}</h3>
      </div>
      <p className="text-gray-500 text-sm mb-4">
        {t('DRAG_AND_DROP_FOR_BULK_SHIPMENTS_RERATE')}
      </p>
      <a
        onClick={() => FileDownload(csvTemplate, 'bulk-shipment-rerate.csv')}
        type="button"
        title={t('DOWNLOAD_TEMPLATE')}
        className="button button-green flex-grow-0 flex-shrink-0 inline-block mb-8"
        download
      >
        <div className="flex space-x-1 items-center">
          <span>{t('TEMPLATE')}</span>
          <DownloadIcon className="w-5 h-5" />
        </div>
      </a>
      <div className="max-w-md">
        {error && (
          <div className="my-4">
            <ErrorAlert
              title={error}
              canClose
              onClose={handleCloseErrorAlert}
            />
          </div>
        )}
        {success && (
          <div className="my-4">
            <Alert
              type="success"
              title={t('SHIPMENTS_UPLOADED')}
              canClose
              onClose={handleCloseSuccessAlert}
            >
              {success}
            </Alert>
          </div>
        )}
        {pending && (
          <div className="my-4">
            <LoadingCircle text={t('UPLOADING') + '...'} />
          </div>
        )}
      </div>
      <div className="sm:max-w-sm">
        <div className="col-span-4 sm:col-span-2">
          <CargomaticInput
            className="mb-4"
            type="email"
            value={emails}
            label={t('RERATE_DATA_EMAIL_INFO')}
            placeholder={t('COMMA_SEPARATED_EMAILS')}
            onChange={handleEmailAddress}
          />
        </div>

        <CargomaticDropzone
          onDrop={addFiles}
          multiple={false}
          style={dropzoneStyle}
          dropzoneMessage={t('DROPZONE_MSG')}
          disabled={pending}
          acceptedFiles=".csv, text/csv, application/vnd.ms-excel, application/csv, text/x-csv, application/x-csv, text/comma-separated-values, text/x-comma-separated-values"
        />
        {files && files.length > 0 && (
          <div className="mt-3">
            <Badge color="blue" withRemove onRemove={removeFiles}>
              {files[0].name}
            </Badge>
          </div>
        )}
        {renderErrors()}
        <div className="p-4 flex justify-end mt-6 space-x-2">
          <button className="button button-green" onClick={handleUpload}>
            {t('SUBMIT')}
          </button>
        </div>
      </div>
    </Container>
  );
};

export default BulkShipmentRerate;
